import { useState, type KeyboardEvent } from 'react';

import { addressSuggestionsAvailable, type AddressSuggestion } from '../api/address-suggestions';
import { useAddressSuggestions, type SuggestionStatus } from '../hooks/use-address-suggestions';

/** Open/search/keyboard state for an address box. Nothing is highlighted until
 *  the user arrows down, so Enter on a hand-typed address never swaps it for
 *  a suggestion. */
export function useAddressAutocomplete(onPick: (address: AddressSuggestion) => void) {
  const available = addressSuggestionsAvailable();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [active, setActive] = useState(-1);
  const { status, suggestions } = useAddressSuggestions(typed, available && open);

  const close = () => {
    setOpen(false);
    setActive(-1);
  };
  const search = (text: string) => {
    setTyped(text);
    setOpen(true);
    setActive(-1);
  };
  const pick = (address: AddressSuggestion) => {
    onPick(address);
    close();
  };
  const move = (step: number) =>
    setActive((i) => Math.max(0, Math.min(i + step, suggestions.length - 1)));

  const listOpen = open && status !== 'idle';
  const onKeyDown = (e: KeyboardEvent) => {
    if (!listOpen) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      move(e.key === 'ArrowDown' ? 1 : -1);
    }
    if (e.key === 'Enter' && suggestions[active]) {
      e.preventDefault();
      pick(suggestions[active]);
    }
  };

  return { available, listOpen, status, suggestions, active, search, pick, close, onKeyDown };
}

export function suggestionNote(status: SuggestionStatus, count: number): string | null {
  if (status === 'loading') return 'Searching addresses…';
  if (status === 'failed') return 'Address suggestions are unavailable. Type the address instead.';
  if (status === 'ready' && count === 0) return 'No matching addresses.';
  return null;
}
